import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import DocumentUi from "../DocumentUi";
import DocumentCamera from "../DocumentCamera";
import styles from "../../../../styles/index.module.css";
import { DocumentCallback } from "@innovatrics/dot-document-auto-capture/.";
import { convertBlobToBase64, getUserDevice, primaryDocumentOptions, resizeBlob } from "../../../../utils/helper";
import { useClient } from "../../../../provider/ClientProvider";
import Image from "../../../common/Image";
import commonStyles from "../../../../styles/common.module.css";
import popupStyles from "../../../../styles/common.module.css";
import {
  FAILURES,
  ICaptureTimestamp,
  IImageFormat,
  IImageQuality,
  SecondaryDocumentCaptureStep,
} from "../../../../types/onboard";
import Failure from "../../common/Failure";
import Success from "../../common/Success";
import SelectComponent from "./Select";
import { SecondaryDocumentCategories } from "../../../../types/document";
import { useTranslation } from "react-i18next";

interface Props {
  id: string | undefined;
  onError: (error: Error) => void;
  photoUrl: string | undefined;
  setPhotoUrl: Dispatch<SetStateAction<string | undefined>>;
  handleNextOnBoardStep: () => Promise<void>;
  setImageFormat: Dispatch<SetStateAction<IImageFormat>>;
  setCaptureTimeStamp: Dispatch<SetStateAction<ICaptureTimestamp>>;
  setImageQuality: Dispatch<SetStateAction<IImageQuality>>
}

const SecondaryDocumentCapture = ({
  id,
  onError,
  setPhotoUrl,
  photoUrl,
  handleNextOnBoardStep,
  setCaptureTimeStamp,
  setImageFormat,
  setImageQuality
}: Props) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selected, setSelected] = useState("");
  const [documentUploadedResponse, setDocumentUploadedResponse] = useState("");
  const {t} = useTranslation();

  const PD_TYPE = sessionStorage.getItem("PD_Type") as string;

  // IDV-4: the secondary document is chosen from the same accepted list, minus
  // whatever was already used as the primary.
  const { client } = useClient();
  const acceptedOptions =
    client?.acceptedDocuments.map((d) => ({ value: d.documentKey, label: d.name })) ??
    primaryDocumentOptions;

  const filteredPrimaryOptions = acceptedOptions.filter(
    (option) => option.value != PD_TYPE
  );

  const options = [
  ...filteredPrimaryOptions,
  {
    value: SecondaryDocumentCategories.BIRTH_CERTIFICATE,
    label: t('secondarydocument.select.option1'),
  },
  ];

   const documentUIInstructions = { 
    document_too_far:t('document.capture.movecloser'),
    sharpness_too_low:t('document.capture.movetobrighter'),
    brightness_too_low:t('document.capture.movetobrighter'),
    candidate_selection:t('document.capture.holdstill'),
    document_centering:t('document.capture.placeinrectangle'),
    document_not_present:t('document.capture.placeinrectangle'),
    brightness_too_high:t('document.capture.lesslight'),
    hotspots_present:t('document.capture.avoidreflections'), 
  }

  const handlePhotoTaken: DocumentCallback = async (imageData, content) => {
    const imageBlob = imageData.image; // Blob of captured secondary image
    const maxSizeMB = 5;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    // Create object URL for preview
    const imageUrl = URL.createObjectURL(imageBlob);
    setPhotoUrl(imageUrl);

    // Extract format and timestamp
    const capturedTimeStamp = Date.now();
    const format = imageBlob?.type?.split("/")[1]?.toUpperCase();

    const imageQuality = imageData?.data?.detection?.sharpness * 100;
    setImageFormat((prev) => ({ ...prev, secondaryDocument: format }));
    setCaptureTimeStamp((prev) => ({
      ...prev,
      secondaryDocument: capturedTimeStamp,
    }));
    setImageQuality((prev) => ({
      ...prev,
      secondaryDocument: imageQuality,
    }));

    // Process image based on its size
    if (imageBlob.size > maxSizeBytes) {
      resizeBlob(imageBlob, maxSizeBytes)
        .then((resizedBase64) => storeImageInSession(resizedBase64))
        .catch((error) => console.error("Error resizing image:", error));
    } else {
      convertBlobToBase64(imageBlob)
        .then((base64) => storeImageInSession(base64))
        .catch((error) => console.error("Error converting image:", error));
    }
  };

  // Store image in sessionStorage
  const storeImageInSession = (base64String: string) => {
    sessionStorage.setItem("SD_Image", base64String.split(",")[1]);
    handleSecondaryDocumentCaptureStep(SecondaryDocumentCaptureStep.ON_SUCCESS);
  };

  const handleSecondaryDocumentCaptureStep = async (stepNumber: number) => {
    setPhotoUrl(undefined);
    setCurrentStep(stepNumber);
  };

  const handleNextStep = async () => {
    setPhotoUrl(undefined);
    setCurrentStep((prevStep) => Math.min(prevStep + 1, 4));
  };

    useEffect(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' }); 
    }, [currentStep]);

  useEffect(() => {
    sessionStorage.setItem("SD_Type", selected);
  }, [selected]);

  const renderStep = (currentStep: SecondaryDocumentCaptureStep) => {
    switch (currentStep) {
      case SecondaryDocumentCaptureStep.SELECT:
        return (
          <div className={commonStyles.secondaryDocumentContainer}>
           {getUserDevice() === "MOBILE" && <h1 className={commonStyles.header}>{t('secondarydocument.title')}</h1>}
            <div className={commonStyles.description}>
             <strong>{t('secondarydocument.select.header')}</strong> 
            </div>
            <SelectComponent
              label={t('secondarydocument.select.placeholder')}
              options={options}
              selectedValue={selected}
              onChange={setSelected}
            />
            <button
              onClick={() => {
                if (selected != "") {
                  handleNextStep();
                }
              }}
              className={`${selected != "" ? commonStyles.button_primary : commonStyles.button_primary_disabled}`}
            >
              {t('button.continue')}
            </button>
          </div>
        );
      case SecondaryDocumentCaptureStep.CAPTURE:
        return (
          <>
            <h1 className={commonStyles.header}>{t('secondarydocument.title')}</h1>
            <span className={commonStyles.description}>
              {t('secondarydocument.description')}
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                flexWrap: "wrap",
                marginTop: "20px",
              }}
            >
              {/* parent container must have position: relative */}
              <div className={styles.container}>
                <DocumentCamera
                  sessionToken={sessionToken}
                  cameraFacing="environment"
                  onPhotoTaken={handlePhotoTaken}
                  onError={onError}
                />
                <DocumentUi
                  showCameraButtons
                  appStateInstructions={{
                    loading: { text: `${t('document.capture.loading')}`, visible: true },
                    waiting: {
                      visible: false,
                    },
                  }}
                  instructions={documentUIInstructions}
                />
                <div className={popupStyles.popup}>
                  {photoUrl && (
                    <Image
                      src={photoUrl}
                      className={popupStyles.popupImg}
                    ></Image>
                  )}
                </div>
              </div>
            </div>
          </>
        );
      case SecondaryDocumentCaptureStep.ON_FAILURE:
        return (
          <Failure
            description={documentUploadedResponse}
            header={t(FAILURES.SECONDARY_DOCUMENT_CAPTURE)}
            body={t('secondarydocument.default.tip')}
            onClick={() => handleSecondaryDocumentCaptureStep(1)}
            buttonLabel={t('button.retry')}
            onContinue={() => {
              handleSecondaryDocumentCaptureStep(
                SecondaryDocumentCaptureStep.ON_SUCCESS
              );
            }}
          />
        );
      case SecondaryDocumentCaptureStep.ON_SUCCESS:
        return (
          <Success
            header={t('secondarydocument.success.header')}
            body={
              <span className={commonStyles.description}>
                {t('success.body')}<strong>{t('success.continue')}</strong> {t('secondarydocument.success.nextstep')}
              </span>
            }
            onClick={handleNextOnBoardStep}
            buttonLabel={t('button.continue')}
          />
        );
    }
  };

  const sessionToken = sessionStorage.getItem("sessionToken") as string;
  return <>{renderStep(currentStep)}</>;
};

export default SecondaryDocumentCapture;
